import { useMutation } from "@apollo/client/react";
import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import type { DryRunOptions, JobLogLevel } from "~/common/types";
import type { JobRun } from "~/components/job-log";
import {
  GetBatchEvidencesJobsDocument,
  SubmitBatchEvidencesJobDocument,
  type SubmitBatchEvidencesJobMutation,
  type SubmitBatchEvidencesJobMutationVariables,
} from "~/features/dataImportProjects/gqls";
import { BatchEvidencesJobType } from "~/features/dataImportProjects/types";
import { useTenant } from "~/features/tenant/context";
import { useActionWithConfirm } from "~/lib/handleActionWithConfirm";

type UseSubmitBatchEvidencesJobProps = {
  jobId: string;
  jobType?: BatchEvidencesJobType;
  importerDeactivatedAt?: string | null;
  refetch: () => void;
  setLogDialogJobRun?: (jobRun: JobRun | null) => void;
  skipConfirm?: boolean;
};

export const useSubmitBatchEvidencesJob = ({
  jobId,
  jobType,
  importerDeactivatedAt,
  refetch,
  setLogDialogJobRun,
  skipConfirm = false,
}: UseSubmitBatchEvidencesJobProps) => {
  const { t } = useTranslation();
  const actionWithConfirm = useActionWithConfirm();
  const [processing, setProcessing] = React.useState<{ value: boolean; dryRun: boolean }>({
    value: false,
    dryRun: false,
  });
  const [submitJob] = useMutation<SubmitBatchEvidencesJobMutation, SubmitBatchEvidencesJobMutationVariables>(
    SubmitBatchEvidencesJobDocument,
    {
      // The list query requires tenantId + filter variables; callers already trigger a targeted `refetch()`.
      // Keeping this empty avoids accidental bare refetches that would fail variable validation.
      refetchQueries: [],
    },
  );
  const { tenantId } = useTenant();
  const submit = useCallback(
    async (dryRun: boolean, dryRunOptions?: DryRunOptions, logLevel?: JobLogLevel) => {
      if (jobType === BatchEvidencesJobType.DbBasedScript && importerDeactivatedAt) {
        throw new Error(t("feature.dataImportProjects.detail.jobs.deactivatedEvidenceImporterSettings"));
      }

      setProcessing({ value: true, dryRun });
      const res = await actionWithConfirm({
        skipConfirm: dryRun || skipConfirm,
        confirmMessage: t("feature.dataImportProjects.detail.jobs.confirmEvidencesJobRun"),
        successMessage: t("feature.dataImportProjects.detail.notify.submitJobSuccessfully"),
        action: async () => {
          if (!tenantId) {
            throw new Error("Tenant is not resolved");
          }

          const { data } = await submitJob({
            variables: { input: { tenantId, jobId, dryRun, dryRunOptions, logLevel } },
          });
          const ret = data?.cnutSubmitBatchEvidencesJob;
          if (!ret) {
            throw new Error(`failed to submit batch evidences job`);
          }
          if ("code" in ret) {
            throw new Error(`error message: ${ret.message}, code: ${ret.code}`);
          }
          return ret;
        },
        successAction: (result) => {
          // if dryRun, open log-viewer dialog
          if (result && result.jobRunId && setLogDialogJobRun) {
            setLogDialogJobRun({
              jobRunId: result.jobRunId as string,
              jobName: "BatchEvidencesJob",
              dryRun,
            });
          }
        },
        finallyAction: () => {
          setProcessing({ value: false, dryRun: false });
        },
      });
      refetch();

      return res;
    },
    [
      actionWithConfirm,
      jobId,
      submitJob,
      t,
      jobType,
      importerDeactivatedAt,
      refetch,
      setProcessing,
      setLogDialogJobRun,
      skipConfirm,
      tenantId,
    ],
  );

  return {
    submit,
    processing,
  };
};
